"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { Student } from "../../../../lib/students-data"
import { nigerianClasses, careerPaths } from "../../../../lib/students-data"

interface EditStudentModalProps {
  isOpen: boolean
  onClose: () => void
  onUpdate: (student: Student) => void
  student: Student | null
}

export function EditStudentModal({ isOpen, onClose, onUpdate, student }: EditStudentModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    class: nigerianClasses[0],
    gpa: "",
    careerPath: careerPaths[0].name,
    guardianName: "",
    guardianPhone: "",
  })

  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name,
        class: student.class,
        gpa: student.gpa,
        careerPath: student.careerPath,
        guardianName: student.guardianName,
        guardianPhone: student.guardianPhone,
      })
    }
  }, [student])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!student) return

    if (!formData.name || !formData.gpa || !formData.guardianName || !formData.guardianPhone) {
      alert("Please fill in all required fields.")
      return
    }

    const selectedCareer = careerPaths.find((c) => c.name === formData.careerPath) || careerPaths[0]

    const updatedStudent: Student = {
      ...student,
      name: formData.name,
      class: formData.class,
      gpa: formData.gpa,
      careerPath: formData.careerPath,
      careerIcon: selectedCareer.icon,
      guardianName: formData.guardianName,
      guardianPhone: formData.guardianPhone,
    }

    onUpdate(updatedStudent)
    onClose()
  }

  if (!isOpen || !student) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Edit Student Info</h3>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editStudentName">
                    Student Name
                  </label>
                  <Input
                    id="editStudentName"
                    placeholder="e.g. Oluwaseun Musa"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editStudentClass">
                      Class
                    </label>
                    <select
                      id="editStudentClass"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={formData.class}
                      onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                    >
                      {nigerianClasses.map((cls) => (
                        <option key={cls} value={cls}>
                          {cls}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editStudentGrade">
                      GPA / Grade
                    </label>
                    <Input
                      id="editStudentGrade"
                      placeholder="e.g. 4.2"
                      value={formData.gpa}
                      onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editCareerPath">
                    Career Path Interest
                  </label>
                  <select
                    id="editCareerPath"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={formData.careerPath}
                    onChange={(e) => setFormData({ ...formData, careerPath: e.target.value })}
                  >
                    {careerPaths.map((career) => (
                      <option key={career.name} value={career.name}>
                        {career.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Details</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      id="editGuardianName"
                      placeholder="Full Name"
                      value={formData.guardianName}
                      onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                      className="w-full"
                    />
                    <Input
                      id="editGuardianPhone"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.guardianPhone}
                      onChange={(e) => setFormData({ ...formData, guardianPhone: e.target.value })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 hover:bg-gray-100 text-gray-700"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Update Student
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}